import { FlashList, FlashListProps, FlashListRef } from '@shopify/flash-list';
import React, { useCallback, useMemo } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  View,
  type ScrollViewProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { Skeleton } from '@/components/ui/skeleton';
import { useScaling } from '@/hooks';
import { useInitState } from '@/hooks/use-init-state';
import { HorizontalScrollView } from '../scroll/horizontal-scroll-view';
import type { BoxStyleOptions } from '../types';
import { buildBoxStyle } from '../utils';

export type GridListMasonryProps<T> = Omit<BoxStyleOptions, 'gap' | 'gapX' | 'gapY'> & {
  data: T[];
  renderItem: (info: { item: T; index: number }) => React.ReactNode;
  keyExtractor?: (item: T, index: number) => string;
  /** Forces list items to re-render when changed. */
  extraData?: FlashListProps<T>['extraData'];
  /** Responsive column count. Accepts a number or `{ phone, tablet }`. Defaults to 2. */
  numColumns?: number | { phone: number; tablet: number };
  /** Uniform spacing between items on both axes */
  gap?: number;
  /** Horizontal spacing between columns (overrides `gap`) */
  gapX?: number;
  /** Vertical spacing between rows (overrides `gap`) */
  gapY?: number;
  style?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  ListHeaderComponent?: FlashListProps<T>['ListHeaderComponent'];
  ListFooterComponent?: FlashListProps<T>['ListFooterComponent'];
  ListEmptyComponent?: FlashListProps<T>['ListEmptyComponent'];
  onEndReached?: FlashListProps<T>['onEndReached'];
  onEndReachedThreshold?: FlashListProps<T>['onEndReachedThreshold'];
  showsVerticalScrollIndicator?: boolean;
  bounces?: boolean;
  refreshControl?: FlashListProps<T>['refreshControl'];
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  scrollEventThrottle?: number;
  maintainVisibleContentPosition?: FlashListProps<T>['maintainVisibleContentPosition'];
  contentInsetAdjustmentBehavior?: ScrollViewProps['contentInsetAdjustmentBehavior'];
  /** If true, FlashList will try to reduce difference in column height by modifying item order. */
  optimizeItemArrangement?: boolean;
  enableLazyLoadingList?: boolean;
  horizontal?: boolean;
};

type GridListMasonryComponent = <T>(
  props: GridListMasonryProps<T> & React.RefAttributes<FlashListRef<T>>,
) => React.ReactElement;

const GridListMasonryInner = <T,>(
  {
    data,
    renderItem,
    keyExtractor,
    extraData,
    numColumns: numColumnsProp = 2,
    gap: gapProp,
    gapX: gapXProp,
    gapY: gapYProp,
    style,
    contentContainerStyle,
    padding,
    paddingX,
    paddingY,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    margin,
    marginX,
    marginY,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    backgroundColor,
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    overflow,
    borderRadius,
    borderWidth,
    borderColor,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    position,
    zIndex,
    bottom,
    left,
    right,
    top,
    maintainVisibleContentPosition,
    enableLazyLoadingList,
    optimizeItemArrangement = false,
    horizontal = false,
    ...rest
  }: GridListMasonryProps<T>,
  ref: React.ForwardedRef<FlashListRef<T>>,
) => {
  const { isTablet } = useScaling();

  const { isInitialRender } = useInitState();

  const cols = useMemo(
    () =>
      typeof numColumnsProp === 'object'
        ? isTablet
          ? numColumnsProp.tablet
          : numColumnsProp.phone
        : numColumnsProp,
    [numColumnsProp, isTablet],
  );

  const hGap = gapXProp ?? gapProp ?? 0;
  const vGap = gapYProp ?? gapProp ?? 0;
  const isGrid = cols > 1;

  const outerStyle = useMemo(
    () =>
      buildBoxStyle({
        margin,
        marginX,
        marginY,
        marginTop,
        marginBottom,
        marginLeft,
        marginRight,
        flex,
        flexGrow,
        flexShrink,
        flexBasis,
        backgroundColor,
        width,
        height,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        overflow,
        borderRadius,
        borderWidth,
        borderColor,
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        position,
        zIndex,
        bottom,
        left,
        right,
        top,
      }),
    [
      margin,
      marginX,
      marginY,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      flex,
      flexGrow,
      flexShrink,
      flexBasis,
      backgroundColor,
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      overflow,
      borderRadius,
      borderWidth,
      borderColor,
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
      position,
      zIndex,
      bottom,
      left,
      right,
      top,
    ],
  );

  const innerStyle = useMemo(
    () =>
      buildBoxStyle({
        padding,
        paddingX,
        paddingY,
        paddingTop,
        paddingBottom,
        paddingLeft,
        paddingRight,
      }),
    [padding, paddingX, paddingY, paddingTop, paddingBottom, paddingLeft, paddingRight],
  );

  const getItemWrapperStyle = useCallback(
    (index: number): ViewStyle | undefined => {
      if (!isGrid) return undefined;
      if (hGap <= 0 && vGap <= 0) return undefined;
      const col = index % cols;
      return {
        paddingLeft: (col * hGap) / cols,
        paddingRight: ((cols - 1 - col) * hGap) / cols,
        paddingTop: index < cols ? 0 : vGap,
      };
    },
    [isGrid, cols, hGap, vGap],
  );

  const gridRenderItem = useCallback(
    ({ item, index }: { item: T; index: number }) => {
      const element = renderItem({ item, index });
      const wrapperStyle = getItemWrapperStyle(index);
      if (wrapperStyle) {
        return <View style={wrapperStyle}>{element}</View>;
      }
      return element as React.ReactElement | null;
    },
    [renderItem, getItemWrapperStyle],
  );

  if (isInitialRender && enableLazyLoadingList) {
    const skeletonRows = isGrid ? 4 : 12;
    const totalSkeletons = skeletonRows * cols;

    return (
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={[styles.skeletonContainer, outerStyle, style] as StyleProp<ViewStyle>}
        contentContainerStyle={[innerStyle, contentContainerStyle] as StyleProp<ViewStyle>}>
        <Skeleton.Group gap={vGap || 8}>
          {isGrid
            ? Array.from({ length: skeletonRows }).map((_, rowIdx) => (
                <View key={rowIdx} style={[SKELETON_ROW, { gap: hGap }]}>
                  {Array.from({ length: cols }).map((__, colIdx) => (
                    <View key={colIdx} style={FLEX_ITEM}>
                      <Skeleton.PlayerCard />
                    </View>
                  ))}
                </View>
              ))
            : Array.from({ length: totalSkeletons }).map((_, index) => (
                <Skeleton.PlayerCard key={index} />
              ))}
        </Skeleton.Group>
      </ScrollView>
    );
  }

  return (
    <FlashList
      ref={ref}
      data={data}
      renderItem={gridRenderItem}
      keyExtractor={keyExtractor}
      extraData={extraData}
      numColumns={cols}
      horizontal={horizontal}
      renderScrollComponent={horizontal ? HorizontalScrollView : undefined}
      masonry
      optimizeItemArrangement={optimizeItemArrangement}
      style={[outerStyle, style] as StyleProp<ViewStyle>}
      contentContainerStyle={[innerStyle, contentContainerStyle] as StyleProp<ViewStyle>}
      maintainVisibleContentPosition={maintainVisibleContentPosition}
      {...rest}
    />
  );
};

export const GridListMasonry = React.forwardRef(GridListMasonryInner) as GridListMasonryComponent;

const FLEX_ITEM: ViewStyle = { flex: 1 };
const SKELETON_ROW: ViewStyle = { flexDirection: 'row' };
const styles = StyleSheet.create({
  skeletonContainer: {
    marginHorizontal: 16,
  },
});
